import { useState, useEffect, useRef, type ReactNode } from 'react'
import * as m from 'motion/react-m'
import { AnimatePresence } from 'motion/react'
import DiscoverMenu from '@/components/navigation/menus/DiscoverMenu'
import DealsMenu from '@/components/navigation/menus/DealsMenu'
import ComparisonsMenu from '@/components/navigation/menus/ComparisonsMenu'
import StacksMenu from '@/components/navigation/menus/StacksMenu'
import CoursesMenu from '@/components/navigation/menus/CoursesMenu'
import Link from '@/components/ui/Link'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { easeShort } from '@/lib/transitions'
import { useHeaderContext } from '@/context/HeaderContext'
import useMediaQuery from '@/hooks/useMediaQuery'
import { useDiscoverMenuContext } from '@/context/NavbarContext'

type SubMenuMainProps = {
  focusedRect: DOMRect
}

type MenuFooterProps = {
  children: ReactNode
  icon?: ReactNode
  iconClass?: string
  cta: string
  link: string
}

export default function SubMenu({ focusedRect }: SubMenuMainProps) {
  const { focus } = useHeaderContext()
  const { isMd, isLg } = useMediaQuery()
  const [prevFocus, setPrevFocus] = useState<string | null>(focus)

  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPosition, setMenuPosition] = useState<number | null>(null)

  const { dealCount, stackCount, comparisonCount = 0 } = useDiscoverMenuContext()

  useEffect(() => {
    if (!menuRef.current) return

    function calculatePosition() {
      if (!menuRef.current) return
      const newPosition =
        isMd || isLg
          ? window.innerWidth / 2 - menuRef.current.offsetWidth / 2
          : focusedRect.left + focusedRect.width / 2 - menuRef.current.offsetWidth / 2
      setMenuPosition(newPosition)
    }

    const resizeObserver = new ResizeObserver(() => {
      calculatePosition()
    })

    const handleWindowResize = () => {
      calculatePosition()
    }

    resizeObserver.observe(menuRef.current)

    window.addEventListener('resize', handleWindowResize)
    calculatePosition()

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [focus, focusedRect, isMd, isLg])

  const menuOrder = ['Discover', 'Deals', 'Stacks', 'Comparisons', 'Courses']

  const getMenuPosition = (menuType: string | null) => {
    if (!menuType) return -1
    return menuOrder.indexOf(menuType)
  }

  const getAnimationDirection = () => {
    const currentPos = getMenuPosition(focus)
    const prevPos = getMenuPosition(prevFocus)
    return currentPos > prevPos ? { enter: 50, exit: -50 } : { enter: -50, exit: 50 }
  }

  const renderMenu = (type: string | null) => {
    switch (type) {
      case 'Discover':
        return <DiscoverMenu />
      case 'Deals':
        return <DealsMenu />
      case 'Stacks':
        return <StacksMenu />
      case 'Comparisons':
        return <ComparisonsMenu />
      case 'Courses':
        return <CoursesMenu />
      default:
        return null
    }
  }

  const renderFooter = () => {
    switch (focus) {
      case 'Discover':
        return (
          <MenuFooter
            icon={<Icon name="hexagon-check" />}
            iconClass="text-success-500"
            cta="Learn more about us"
            link="/about"
          >
            Curated, tested, and written by our experts, <span>Alex</span> and <span>Andra</span>.
          </MenuFooter>
        )
      case 'Deals':
        return (
          <MenuFooter
            icon={<Icon name="deal" size={12} />}
            iconClass="text-primary-500"
            cta="View all deals"
            link="/deals"
          >
            See all the <span>{dealCount}</span> app deals
          </MenuFooter>
        )
      case 'Stacks':
        return (
          <MenuFooter
            icon={<Icon name="stack" size={12} />}
            iconClass="text-primary-500"
            cta="View all stacks"
            link="/stacks"
          >
            See all <span>{stackCount}</span> stacks
          </MenuFooter>
        )
      case 'Comparisons':
        return (
          <MenuFooter
            icon={<Icon name="comparison" size={12} />}
            iconClass="text-primary-500"
            cta="View all comparisons"
            link="/compare"
          >
            See all <span>{comparisonCount}</span> comparisons
          </MenuFooter>
        )
      default:
        return null
    }
  }

  useEffect(() => {
    setPrevFocus(focus)
  }, [focus])

  const direction = getAnimationDirection()

  return (
    <m.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={easeShort}
      className="fixed top-[60px] z-50 origin-top"
      style={{
        left: menuPosition !== null ? `${menuPosition}px` : undefined,
        visibility: menuPosition !== null ? 'visible' : 'hidden',
      }}
    >
      <div
        ref={menuRef}
        className="rounded-2xl border border-dark-100 bg-white/90 p-2.5 !text-dark-950 shadow-xl backdrop-blur-[6px] dark:border-white/10 dark:bg-black/90"
      >
        <div className="w-full rounded-menu border border-gray-200 bg-white dark:border-white/10 dark:bg-neutral-950">
          <AnimatePresence mode="wait" initial={false}>
            {focus && (
              <m.div
                key={focus}
                initial={{ x: direction.enter, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: direction.exit, opacity: 0 }}
                transition={easeShort}
              >
                {renderMenu(focus)}
              </m.div>
            )}
          </AnimatePresence>
        </div>
        {renderFooter()}
      </div>
    </m.div>
  )
}

export function MenuTitle({
  title,
  className,
  back,
}: {
  title: string
  className?: string
  back?: boolean
}) {
  const { stage, resetStage } = useHeaderContext()

  const handleBack = () => {
    resetStage()
  }

  const allowBack = back && stage > 0

  return (
    <h2
      className={cn(
        'flex h-5 items-center px-5 py-1 text-[11px] font-medium uppercase leading-none text-zinc-500',
        allowBack &&
          'cursor-pointer gap-4 transition-colors hover:text-gray-950 dark:hover:text-white',
        className,
      )}
      onClick={allowBack ? handleBack : undefined}
    >
      {allowBack && <Icon name="arrow-left" />}
      {title}
    </h2>
  )
}

export function MenuFooter({ children, icon, iconClass, cta, link }: MenuFooterProps) {
  const { setShowMenu } = useHeaderContext()

  return (
    <div className="mt-3 flex h-10 items-center justify-between pr-3">
      <div className="flex items-center gap-3.5">
        <div
          className={cn(
            'flex h-[26px] w-8.5 items-center justify-center gap-2 rounded-full border border-gray-100 bg-gray-50 dark:border-white/10 dark:bg-white/5',
            iconClass,
          )}
        >
          {icon}
        </div>
        <div className="text-13 text-zinc-500 [&>span]:font-medium [&>span]:text-dark-950 dark:[&>span]:text-white">
          {children}
        </div>
      </div>

      {cta && link && (
        <Link
          href={link}
          onClick={() => setShowMenu(false)}
          theme="primary"
          className="underlined primary text-13"
        >
          {cta}
        </Link>
      )}
    </div>
  )
}
